if (!customElements.get('monarch-saddle-picker')) {
  class MonarchSaddlePicker extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;

      this.dataset.initialized = 'true';

      this.variantPicker = this.closest('variant-picker');
      this.railOptionName = this.dataset.railOption;
      this.colourOptionName = this.dataset.colourOption;
      this.railOptionIndex = Number(this.dataset.railIndex);
      this.colourOptionIndex = Number(this.dataset.colourIndex);

      this.railButtons = Array.from(
        this.querySelectorAll('[data-monarch-rail]')
      );

      this.colourButtons = Array.from(
        this.querySelectorAll('[data-monarch-colour]')
      );

      this.selectedColourText = this.querySelector(
        '[data-monarch-selected-colour]'
      );

      this.section = this.closest('.js-product');
      this.productForm = this.section
        ? this.section.querySelector('.js-product-form')
        : null;

      if (
        !this.variantPicker ||
        !this.railOptionName ||
        !this.colourOptionName ||
        !Number.isInteger(this.railOptionIndex) ||
        !Number.isInteger(this.colourOptionIndex)
      ) {
        return;
      }

      this.variantData = this.getVariantData();
      this.preloadedRailImages = [];
      this.mediaGallery = this.findMediaGallery();
      this.setupCleanGalleryThumbnails();
      this.setupGalleryVideos();
      this.watchGalleryVideos();
      this.setupAdaptiveGallerySticky();
      this.setupVariantImageHoverZoom();
      this.setupRailCardHoverPreview();

      this.handleClick = this.handleClick.bind(this);
      this.handleVariantChange = this.handleVariantChange.bind(this);

      this.addEventListener('click', this.handleClick);

      this.variantPicker.addEventListener(
        'on:variant:change',
        this.handleVariantChange
      );

      this.syncFromNativeOptions();
      this.scheduleRailImagePreload();
    }

    disconnectedCallback() {
      if (this.handleClick) {
        this.removeEventListener('click', this.handleClick);
      }

      if (this.variantPicker && this.handleVariantChange) {
        this.variantPicker.removeEventListener(
          'on:variant:change',
          this.handleVariantChange
        );
      }

      if (this.galleryActivationFrame) {
        window.cancelAnimationFrame(
          this.galleryActivationFrame
        );
      }

      if (this.galleryVideoObserver) {
        this.galleryVideoObserver.disconnect();
        this.galleryVideoObserver = null;
      }

      if (this.galleryVideoProgressFrame) {
        window.cancelAnimationFrame(
          this.galleryVideoProgressFrame
        );
        this.galleryVideoProgressFrame = null;
      }

      if (this.galleryVideoProgressOverlay) {
        this.galleryVideoProgressOverlay.remove();
        this.galleryVideoProgressOverlay = null;
        this.galleryVideoProgressFill = null;
      }

      if (this.galleryStickyFrame) {
        window.cancelAnimationFrame(
          this.galleryStickyFrame
        );
        this.galleryStickyFrame = null;
      }

      if (this.galleryStickyResizeObserver) {
        this.galleryStickyResizeObserver.disconnect();
        this.galleryStickyResizeObserver = null;
      }

      if (this.galleryStickyResizeHandler) {
        window.removeEventListener(
          'resize',
          this.galleryStickyResizeHandler
        );

        if (window.visualViewport) {
          window.visualViewport.removeEventListener(
            'resize',
            this.galleryStickyResizeHandler
          );
        }

        this.galleryStickyResizeHandler = null;
      }

      if (this.mediaGallery && this.handleVariantZoomPointerMove) {
        this.mediaGallery.removeEventListener(
          'pointermove',
          this.handleVariantZoomPointerMove
        );
        this.mediaGallery.removeEventListener(
          'pointerover',
          this.handleVariantZoomPointerOver
        );
        this.mediaGallery.removeEventListener(
          'pointerout',
          this.handleVariantZoomPointerOut
        );
      }

      if (this.handleRailPreviewPointerOver) {
        this.removeEventListener(
          'pointerover',
          this.handleRailPreviewPointerOver
        );

        this.removeEventListener(
          'pointerout',
          this.handleRailPreviewPointerOut
        );
      }

      if (this.handleRailPreviewResize) {
        window.removeEventListener(
          'resize',
          this.handleRailPreviewResize
        );
      }

      if (this.railPreviewShowTimer) {
        window.clearTimeout(
          this.railPreviewShowTimer
        );
      }

      if (this.railPreviewHideTimer) {
        window.clearTimeout(
          this.railPreviewHideTimer
        );
      }

      if (
        this.railHoverPreview &&
        this.railHoverPreview.parentNode
      ) {
        this.railHoverPreview.parentNode.removeChild(
          this.railHoverPreview
        );
      }

      delete this.dataset.initialized;
    }

    setupAdaptiveGallerySticky() {
      if (!this.mediaGallery) return;

      this.galleryStickyInner =
        this.mediaGallery.querySelector(
          '.media-gallery__inner.sticky-content-container'
        );

      if (!this.galleryStickyInner) return;

      this.galleryStickyMediaQuery =
        window.matchMedia(
          '(min-width: 768px) and (hover: hover) and (pointer: fine)'
        );

      this.galleryStickyResizeHandler = () => {
        this.scheduleAdaptiveGallerySticky();
      };

      window.addEventListener(
        'resize',
        this.galleryStickyResizeHandler,
        { passive: true }
      );

      if (window.visualViewport) {
        window.visualViewport.addEventListener(
          'resize',
          this.galleryStickyResizeHandler,
          { passive: true }
        );
      }

      if ('ResizeObserver' in window) {
        this.galleryStickyResizeObserver =
          new ResizeObserver(() => {
            this.scheduleAdaptiveGallerySticky();
          });

        this.galleryStickyResizeObserver.observe(
          this.galleryStickyInner
        );
      }

      this.scheduleAdaptiveGallerySticky();
    }

    scheduleAdaptiveGallerySticky() {
      if (this.galleryStickyFrame) {
        window.cancelAnimationFrame(
          this.galleryStickyFrame
        );
      }

      this.galleryStickyFrame =
        window.requestAnimationFrame(() => {
          this.galleryStickyFrame = null;
          this.updateAdaptiveGallerySticky();
        });
    }

    updateAdaptiveGallerySticky() {
      const inner = this.galleryStickyInner;

      if (!inner) return;

      if (
        !this.galleryStickyMediaQuery ||
        !this.galleryStickyMediaQuery.matches
      ) {
        inner.style.removeProperty('top');
        return;
      }

      const galleryHeight =
        inner.getBoundingClientRect().height;

      if (!galleryHeight) return;

      const rootStyles =
        window.getComputedStyle(
          document.documentElement
        );

      const stickyHeaderHeight = parseFloat(
        rootStyles.getPropertyValue(
          '--theme-sticky-header-height'
        )
      );

      const headerHeight =
        Number.isFinite(stickyHeaderHeight)
          ? stickyHeaderHeight
          : 0;

      const topGap = 10;
      const bottomGap = 10;

      const preferredTop =
        headerHeight + topGap;

      const availableHeight =
        window.innerHeight -
        preferredTop -
        bottomGap;

      /*
        If the gallery fits inside the viewport, use the normal top-sticky
        position.

        If it is taller than the viewport, let it scroll upward until the
        bottom of the gallery is 10px above the bottom of the screen, then
        stop there. This is the behaviour wanted on the Mac/laptop view:
        no clipping, no inner scrollbar, no resizing.
      */
      const stickyTop =
        galleryHeight <= availableHeight
          ? preferredTop
          : window.innerHeight -
            galleryHeight -
            bottomGap;

      inner.style.setProperty(
        'top',
        `${Math.floor(stickyTop)}px`,
        'important'
      );
    }

    getVariantData() {
      const dataElement = this.variantPicker.querySelector(
        'script[type="application/json"]'
      );

      if (!dataElement) return null;

      try {
        return JSON.parse(dataElement.textContent);
      } catch (error) {
        console.warn(
          'The Monarch Saddle variant picker could not read the Shopify variant data.',
          error
        );

        return null;
      }
    }

    handleClick(event) {
      const compareButton = event.target.closest(
        '[data-monarch-compare-rails]'
      );

      if (compareButton && this.contains(compareButton)) {
        this.openRailComparison();
        return;
      }

      const railButton = event.target.closest('[data-monarch-rail]');

      if (railButton && this.contains(railButton)) {
        this.selectOption(
          this.railOptionName,
          railButton.dataset.railValue
        );
        return;
      }

      const colourButton = event.target.closest('[data-monarch-colour]');

      if (colourButton && this.contains(colourButton)) {
        this.preloadColourImages(colourButton.dataset.colourValue);

        this.selectOption(
          this.colourOptionName,
          colourButton.dataset.colourValue
        );
      }
    }

    selectOption(optionName, optionValue) {
      const input = this.findNativeOptionInput(optionName, optionValue);

      if (!input) {
        console.warn(
          `The Monarch Saddle variant picker could not find ${optionName}: ${optionValue}.`
        );
        return;
      }

      if (!input.checked) {
        input.checked = true;

        input.dispatchEvent(
          new Event('change', {
            bubbles: true
          })
        );
      }

      window.requestAnimationFrame(() => {
        this.syncFromNativeOptions();
      });
    }

    findNativeOptionInput(optionName, optionValue) {
      const selector = Array.from(
        this.variantPicker.querySelectorAll(
          '.monarch-native-options .option-selector[data-selector-type="listed"]'
        )
      ).find((element) => element.dataset.option === optionName);

      if (!selector) return null;

      return (
        Array.from(selector.querySelectorAll('input.js-option')).find(
          (input) => input.value === optionValue
        ) || null
      );
    }

    getCheckedOptionValue(optionName) {
      const selector = Array.from(
        this.variantPicker.querySelectorAll(
          '.monarch-native-options .option-selector[data-selector-type="listed"]'
        )
      ).find((element) => element.dataset.option === optionName);

      if (!selector) return '';

      const checkedInput = selector.querySelector('input.js-option:checked');

      return checkedInput ? checkedInput.value : '';
    }

    handleVariantChange() {
      this.syncFromNativeOptions();
    }

    syncFromNativeOptions() {
      const selectedRail = this.getCheckedOptionValue(this.railOptionName);
      const selectedColour = this.getCheckedOptionValue(
        this.colourOptionName
      );

      this.railButtons.forEach((button) => {
        const isSelected = button.dataset.railValue === selectedRail;

        button.setAttribute(
          'aria-pressed',
          isSelected ? 'true' : 'false'
        );
      });

      this.colourButtons.forEach((button) => {
        const isSelected = button.dataset.colourValue === selectedColour;

        button.setAttribute(
          'aria-pressed',
          isSelected ? 'true' : 'false'
        );
      });

      if (this.selectedColourText) {
        this.selectedColourText.textContent =
          selectedColour || 'Select a colour';
      }

      this.updateRailImages(selectedColour);
      this.updateAvailability(selectedRail, selectedColour);
      this.updateMonarchPrice(selectedRail, selectedColour);
      this.updatePurchaseButton(selectedRail, selectedColour);
      this.updateGalleryMedia(selectedRail, selectedColour);
      this.updateVariantImageHoverZoom(
        selectedRail,
        selectedColour
      );

      const railPreviewReady = Boolean(
        selectedRail && selectedColour
      );

      this.classList.toggle(
        'monarch-picker--rail-preview-ready',
        railPreviewReady
      );

      if (railPreviewReady) {
        this.preloadCurrentRailPreviewPair();
      } else {
        this.hideRailHoverPreview(true);
      }
    }

    findMediaGallery() {
      if (this.section) {
        const gallery = this.section.querySelector('media-gallery');

        if (gallery) return gallery;
      }

      const productContainer =
        this.closest('.product-detail') ||
        this.closest('[data-product-id]') ||
        this.parentElement;

      return productContainer
        ? productContainer.querySelector('media-gallery')
        : null;
    }

    getFeaturedMediaId() {
      if (!this.variantData) return '';

      const mediaId = this.variantData.featuredMediaId;

      return mediaId ? String(mediaId) : '';
    }

    getVariantMediaId(variant) {
      if (
        !variant ||
        !this.variantData ||
        !this.variantData.formatted
      ) {
        return '';
      }

      const formatted =
        this.variantData.formatted[String(variant.id)];

      if (!formatted || !formatted.mediaId) return '';

      return String(formatted.mediaId);
    }

    getAllVariantMediaIds() {
      if (
        !this.variantData ||
        !this.variantData.formatted
      ) {
        return new Set();
      }

      const ids = new Set();

      Object.values(this.variantData.formatted).forEach((variantInfo) => {
        if (variantInfo && variantInfo.mediaId) {
          ids.add(String(variantInfo.mediaId));
        }
      });

      return ids;
    }

    getSelectedVariant(railValue, colourValue) {
      if (!railValue || !colourValue) return null;

      return (
        this.getMatchingVariants(
          railValue,
          colourValue
        )[0] || null
      );
    }

    setupGalleryVideos(root = this.mediaGallery) {
      if (!root) return;

      this.installGalleryVideoStyles();

      const videos = [];

      if (
        root.matches &&
        root.matches(
          '.product-media--video video, .product-media--frame video'
        )
      ) {
        videos.push(root);
      }

      if (root.querySelectorAll) {
        root
          .querySelectorAll(
            '.product-media--video video, .product-media--frame video'
          )
          .forEach((video) => videos.push(video));
      }

      videos.forEach((video) => {
        /*
          Symmetry creates this <video> lazily from a <template>.
          Remove native controls every time we encounter it because its own
          video-component can re-apply the controls attribute during setup.
        */
        video.controls = false;
        video.removeAttribute('controls');
        video.setAttribute(
          'controlsList',
          'nodownload noplaybackrate nofullscreen'
        );
        video.loop = true;
        video.setAttribute('loop', '');
        video.playsInline = true;
        video.setAttribute('playsinline', '');
        video.preload = video.preload || 'metadata';
        video.style.cursor = 'pointer';

        if (!video.hasAttribute('tabindex')) {
          video.setAttribute('tabindex', '0');
        }

        video.setAttribute('role', 'button');
        this.updateGalleryVideoLabel(video);
        this.setupFloatingGalleryVideoProgress(video);

        if (
          video.dataset.monarchClickToggle === 'true'
        ) {
          return;
        }

        video.dataset.monarchClickToggle = 'true';

        const togglePlayback = (event) => {
          if (
            event &&
            event.type === 'keydown' &&
            event.key !== 'Enter' &&
            event.key !== ' '
          ) {
            return;
          }

          if (event && event.type === 'keydown') {
            event.preventDefault();
          }

          /*
            Do not let a click on the video bubble into any theme media
            handling. The video surface itself is now the play/pause control.
          */
          if (event && event.type === 'click') {
            event.preventDefault();
            event.stopPropagation();
          }

          if (video.paused || video.ended) {
            const playPromise = video.play();

            if (
              playPromise &&
              typeof playPromise.catch === 'function'
            ) {
              playPromise.catch(() => {});
            }
          } else {
            video.pause();
          }
        };

        video.addEventListener(
          'click',
          togglePlayback
        );

        video.addEventListener(
          'keydown',
          togglePlayback
        );

        video.addEventListener(
          'play',
          () => {
            this.updateGalleryVideoLabel(video);
            this.startFloatingGalleryVideoProgress(video);
          }
        );

        video.addEventListener(
          'pause',
          () => {
            this.updateGalleryVideoLabel(video);
            this.updateFloatingGalleryVideoProgress(video);
          }
        );

        video.addEventListener(
          'ended',
          () => {
            this.updateGalleryVideoLabel(video);
            this.updateFloatingGalleryVideoProgress(video);
          }
        );

        video.addEventListener(
          'loadedmetadata',
          () => this.updateFloatingGalleryVideoProgress(video)
        );

        this.updateFloatingGalleryVideoProgress(video);

        if (!video.paused) {
          this.startFloatingGalleryVideoProgress(video);
        }
      });
    }

    setupFloatingGalleryVideoProgress(video) {
      if (!video) return;

      if (!this.galleryVideoProgressOverlay) {
        const overlay = document.createElement('div');
        overlay.setAttribute('aria-hidden', 'true');

        Object.assign(overlay.style, {
          position: 'fixed',
          zIndex: '2147483000',
          height: '3px',
          overflow: 'hidden',
          background: 'rgba(5, 195, 221, 0.16)',
          pointerEvents: 'none',
          margin: '0',
          padding: '0',
          display: 'none'
        });

        const fill = document.createElement('div');

        Object.assign(fill.style, {
          width: '100%',
          height: '100%',
          background: '#05c3dd',
          transform: 'scaleX(0)',
          transformOrigin: 'left center',
          willChange: 'transform',
          pointerEvents: 'none'
        });

        overlay.appendChild(fill);
        document.body.appendChild(overlay);

        this.galleryVideoProgressOverlay = overlay;
        this.galleryVideoProgressFill = fill;
      }

      video.monarchHasFloatingProgress = true;
    }

    positionFloatingGalleryVideoProgress(video) {
      const overlay = this.galleryVideoProgressOverlay;

      if (!video || !overlay) return false;

      const slide = video.closest('.slider__item');

      if (
        slide &&
        !slide.classList.contains('is-active')
      ) {
        overlay.style.display = 'none';
        return false;
      }

      const rect = video.getBoundingClientRect();

      if (
        rect.width <= 0 ||
        rect.height <= 0 ||
        rect.bottom <= 0 ||
        rect.top >= window.innerHeight ||
        rect.right <= 0 ||
        rect.left >= window.innerWidth
      ) {
        overlay.style.display = 'none';
        return false;
      }

      /*
        The overlay lives directly under <body> with position:fixed.
        It is therefore completely outside Symmetry's gallery layout and
        cannot change gallery height, width, sticky positioning or thumbnails.
      */
      let visibleWidth = rect.width;
      let visibleHeight = rect.height;
      let insetX = 0;
      let insetY = 0;

      if (
        video.videoWidth > 0 &&
        video.videoHeight > 0
      ) {
        const mediaRatio =
          video.videoWidth / video.videoHeight;
        const boxRatio =
          rect.width / rect.height;

        if (mediaRatio > boxRatio) {
          visibleWidth = rect.width;
          visibleHeight =
            visibleWidth / mediaRatio;
          insetY =
            (rect.height - visibleHeight) / 2;
        } else {
          visibleHeight = rect.height;
          visibleWidth =
            visibleHeight * mediaRatio;
          insetX =
            (rect.width - visibleWidth) / 2;
        }
      }

      Object.assign(overlay.style, {
        display: 'block',
        left: `${rect.left + insetX}px`,
        top: `${rect.top + insetY + visibleHeight - 3}px`,
        width: `${visibleWidth}px`
      });

      return true;
    }

    updateFloatingGalleryVideoProgress(video) {
      if (
        !video ||
        !this.galleryVideoProgressFill
      ) {
        return;
      }

      const isVisible =
        this.positionFloatingGalleryVideoProgress(video);

      if (!isVisible) return;

      const duration = Number(video.duration);
      const currentTime = Number(video.currentTime);

      const progress =
        Number.isFinite(duration) &&
        duration > 0 &&
        Number.isFinite(currentTime)
          ? Math.min(
              1,
              Math.max(0, currentTime / duration)
            )
          : 0;

      this.galleryVideoProgressFill.style.transform =
        `scaleX(${progress})`;
    }

    startFloatingGalleryVideoProgress(video) {
      if (!video) return;

      if (this.galleryVideoProgressFrame) {
        window.cancelAnimationFrame(
          this.galleryVideoProgressFrame
        );
      }

      const tick = () => {
        this.updateFloatingGalleryVideoProgress(video);

        if (!video.paused && !video.ended) {
          this.galleryVideoProgressFrame =
            window.requestAnimationFrame(tick);
        } else {
          this.galleryVideoProgressFrame = null;
        }
      };

      this.galleryVideoProgressFrame =
        window.requestAnimationFrame(tick);
    }

    installGalleryVideoStyles() {
      const styleId =
        'monarch-gallery-video-control-styles';

      if (document.getElementById(styleId)) {
        return;
      }

      const style = document.createElement('style');

      style.id = styleId;
      style.textContent = `
        .media-gallery .product-media--video video,
        .media-gallery .product-media--frame video {
          cursor: pointer;
        }

        .media-gallery .product-media--video video::-webkit-media-controls,
        .media-gallery .product-media--frame video::-webkit-media-controls {
          display: none !important;
          opacity: 0 !important;
          visibility: hidden !important;
        }

        .media-gallery .product-media--video video::-webkit-media-controls-enclosure,
        .media-gallery .product-media--frame video::-webkit-media-controls-enclosure {
          display: none !important;
        }
      `;

      document.head.appendChild(style);
    }

    watchGalleryVideos() {
      if (
        !this.mediaGallery ||
        this.galleryVideoObserver
      ) {
        return;
      }

      this.galleryVideoObserver =
        new MutationObserver((mutations) => {
          mutations.forEach((mutation) => {
            /*
              Shopify's deferred-media clones the real video into the DOM
              only after the poster/play button is activated.
            */
            mutation.addedNodes.forEach((node) => {
              if (node.nodeType !== Node.ELEMENT_NODE) {
                return;
              }

              this.setupGalleryVideos(node);
            });

            /*
              Symmetry's video-component may set controls="controls" after
              insertion. Immediately remove it again if that happens.
            */
            if (
              mutation.type === 'attributes' &&
              mutation.target instanceof HTMLVideoElement &&
              mutation.attributeName === 'controls'
            ) {
              const video = mutation.target;

              if (video.hasAttribute('controls')) {
                video.controls = false;
                video.removeAttribute('controls');
              }
            }
          });
        });

      this.galleryVideoObserver.observe(
        this.mediaGallery,
        {
          subtree: true,
          childList: true,
          attributes: true,
          attributeFilter: ['controls']
        }
      );
    }

    updateGalleryVideoLabel(video) {
      if (!video) return;

      const action =
        video.paused || video.ended
          ? 'Play'
          : 'Pause';

      video.setAttribute(
        'aria-label',
        `${action} product video`
      );
    }

    setupRailCardHoverPreview() {
      this.railPreviewMediaQuery = window.matchMedia(
        '(min-width: 992px) and (hover: hover) and (pointer: fine)'
      );

      [
        'monarch-rail-card-hover-preview-styles',
        'monarch-rail-card-hover-preview-styles-v2',
        'monarch-rail-card-hover-preview-styles-v3'
      ].forEach((styleId) => {
        const oldStyle = document.getElementById(styleId);

        if (oldStyle) {
          oldStyle.remove();
        }
      });

      const styleId =
        'monarch-rail-card-hover-preview-styles-v3';

      const style = document.createElement('style');

      style.id = styleId;
      style.textContent = `
        @media
          (min-width: 992px) and
          (hover: hover) and
          (pointer: fine) {
          .monarch-rail-hover-preview {
            position: fixed;
            z-index: 9999;

            aspect-ratio: 2.12 / 1;

            overflow: hidden;

            background: #ffffff;
            border: 1px solid #d8d8d8;
            border-top: 3px solid #05c3dd;
            box-shadow:
              0 12px 32px rgba(0, 0, 0, 0.11);

            opacity: 0;
            visibility: hidden;
            pointer-events: none;

            transform: translateY(5px) scale(0.992);

            transition:
              opacity 125ms ease,
              transform 145ms ease,
              visibility 0s linear 145ms;
          }

          .monarch-rail-hover-preview--visible {
            opacity: 1;
            visibility: visible;

            transform: translateY(0) scale(1);

            transition:
              opacity 125ms ease,
              transform 145ms ease,
              visibility 0s linear 0s;
          }

          .monarch-rail-hover-preview__image {
            position: absolute;
            inset: 12px 16px;

            display: block;

            width: calc(100% - 32px);
            height: calc(100% - 24px);

            object-fit: contain;
            object-position: center;

            opacity: 0;

            transition:
              opacity 115ms ease;

            user-select: none;
          }

          .monarch-rail-hover-preview__image--active {
            opacity: 1;
          }

          monarch-saddle-picker.monarch-picker--rail-preview-ready
            .monarch-picker__rail-media {
            cursor: zoom-in;
          }
        }

        @media
          (prefers-reduced-motion: reduce) {
          .monarch-rail-hover-preview,
          .monarch-rail-hover-preview__image {
            transition: none !important;
            transform: none !important;
          }
        }
      `;

      document.head.appendChild(style);

      this.railHoverPreview = document.createElement('div');
      this.railHoverPreview.className =
        'monarch-rail-hover-preview';
      this.railHoverPreview.setAttribute(
        'aria-hidden',
        'true'
      );

      this.railHoverPreviewImages = [
        document.createElement('img'),
        document.createElement('img')
      ];

      this.railHoverPreviewImages.forEach(
        (image, index) => {
          image.className =
            'monarch-rail-hover-preview__image';

          if (index === 0) {
            image.classList.add(
              'monarch-rail-hover-preview__image--active'
            );
          }

          image.alt = '';
          image.decoding = 'async';

          this.railHoverPreview.appendChild(image);
        }
      );

      this.railPreviewActiveImageIndex = 0;
      this.railPreviewCurrentSource = '';
      this.railPreviewActiveCardSource = '';
      this.railPreviewShowTimer = null;
      this.railPreviewHideTimer = null;
      this.railPreviewLoadToken = 0;
      this.railPreviewRailMedia = null;

      /*
        Cache the two high-resolution side-profile images for the
        currently selected colour. This means switching from CrMo to
        Ti-MX inside the preview is normally an instant crossfade rather
        than a low-res image sharpening after hover.
      */
      this.railPreviewPreloadCache = new Map();

      document.body.appendChild(
        this.railHoverPreview
      );

      this.handleRailPreviewPointerOver = (event) => {
        if (
          !this.railPreviewMediaQuery ||
          !this.railPreviewMediaQuery.matches
        ) {
          return;
        }

        const railMedia = event.target.closest(
          '.monarch-picker__rail-media'
        );

        if (!railMedia || !this.contains(railMedia)) {
          return;
        }

        const selectedRail = this.getCheckedOptionValue(
          this.railOptionName
        );
        const selectedColour = this.getCheckedOptionValue(
          this.colourOptionName
        );

        if (!selectedRail || !selectedColour) {
          return;
        }

        if (this.railPreviewHideTimer) {
          window.clearTimeout(
            this.railPreviewHideTimer
          );

          this.railPreviewHideTimer = null;
        }

        /*
          If the preview is already open, changing rail cards only
          changes the saddle image. The panel itself never moves.
        */
        if (
          this.railHoverPreview.classList.contains(
            'monarch-rail-hover-preview--visible'
          )
        ) {
          this.showRailHoverPreview(
            railMedia
          );

          return;
        }

        if (this.railPreviewShowTimer) {
          window.clearTimeout(
            this.railPreviewShowTimer
          );
        }

        this.railPreviewShowTimer =
          window.setTimeout(() => {
            this.railPreviewShowTimer = null;

            this.showRailHoverPreview(
              railMedia
            );
          }, 135);
      };

      this.handleRailPreviewPointerOut = (event) => {
        const railMedia = event.target.closest(
          '.monarch-picker__rail-media'
        );

        if (!railMedia || !this.contains(railMedia)) {
          return;
        }

        if (
          event.relatedTarget &&
          railMedia.contains(event.relatedTarget)
        ) {
          return;
        }

        const nextRailMedia =
          event.relatedTarget &&
          event.relatedTarget.closest
            ? event.relatedTarget.closest(
                '.monarch-picker__rail-media'
              )
            : null;

        if (
          nextRailMedia &&
          this.contains(nextRailMedia)
        ) {
          return;
        }

        if (this.railPreviewShowTimer) {
          window.clearTimeout(
            this.railPreviewShowTimer
          );

          this.railPreviewShowTimer = null;
        }

        if (this.railPreviewHideTimer) {
          window.clearTimeout(
            this.railPreviewHideTimer
          );
        }

        this.railPreviewHideTimer =
          window.setTimeout(() => {
            this.railPreviewHideTimer = null;

            this.hideRailHoverPreview();
          }, 85);
      };

      this.handleRailPreviewResize = () => {
        if (
          this.railHoverPreview &&
          this.railHoverPreview.classList.contains(
            'monarch-rail-hover-preview--visible'
          )
        ) {
          this.positionRailHoverPreview();
        }
      };

      this.addEventListener(
        'pointerover',
        this.handleRailPreviewPointerOver,
        { passive: true }
      );

      this.addEventListener(
        'pointerout',
        this.handleRailPreviewPointerOut,
        { passive: true }
      );

      window.addEventListener(
        'resize',
        this.handleRailPreviewResize,
        { passive: true }
      );
    }

    getRailHoverPreviewSource(source) {
      if (!source) return '';

      try {
        const url = new URL(
          source,
          window.location.origin
        );

        if (
          url.hostname.includes(
            'cdn.shopify.com'
          )
        ) {
          url.searchParams.set(
            'width',
            '1000'
          );

          return url.toString();
        }
      } catch (error) {
        /*
          Fall back to the current card URL if it is ever not a
          standard Shopify CDN URL.
        */
      }

      return source
        .replace(
          /([?&])width=\d+/i,
          '$1width=1000'
        )
        .replace(
          /_\d+x(?=\.[a-z0-9]+(?:\?|$))/i,
          '_1000x'
        );
    }

    preloadCurrentRailPreviewPair() {
      if (
        !this.railPreviewPreloadCache ||
        !this.railPreviewMediaQuery ||
        !this.railPreviewMediaQuery.matches
      ) {
        return;
      }

      const sources = Array.from(
        this.querySelectorAll(
          '.monarch-picker__rail-media [data-monarch-rail-image]'
        )
      )
        .map((image) =>
          image.currentSrc ||
          image.getAttribute('src') ||
          ''
        )
        .filter(Boolean)
        .map((source) =>
          this.getRailHoverPreviewSource(source)
        )
        .filter(Boolean);

      [...new Set(sources)].forEach(
        (source) => {
          if (
            this.railPreviewPreloadCache.has(
              source
            )
          ) {
            return;
          }

          const image = new Image();

          image.decoding = 'async';

          if ('fetchPriority' in image) {
            image.fetchPriority = 'low';
          }

          this.railPreviewPreloadCache.set(
            source,
            image
          );

          image.src = source;
        }
      );
    }

    getRailPreviewGroupRect() {
      const visibleRailButtons =
        this.railButtons.filter((button) => {
          const rect =
            button.getBoundingClientRect();

          return (
            rect.width > 0 &&
            rect.height > 0
          );
        });

      if (!visibleRailButtons.length) {
        return null;
      }

      const buttonRects =
        visibleRailButtons.map((button) =>
          button.getBoundingClientRect()
        );

      const left = Math.min(
        ...buttonRects.map((rect) => rect.left)
      );

      const right = Math.max(
        ...buttonRects.map((rect) => rect.right)
      );

      const top = Math.min(
        ...buttonRects.map((rect) => rect.top)
      );

      const bottom = Math.max(
        ...buttonRects.map((rect) => rect.bottom)
      );

      return {
        left,
        right,
        top,
        bottom,
        width: right - left,
        height: bottom - top
      };
    }

    positionRailHoverPreview() {
      if (!this.railHoverPreview) return;

      const groupRect =
        this.getRailPreviewGroupRect();

      if (!groupRect) return;

      const edge = 16;
      const gap = 14;

      /*
        Match the popover width exactly to the full pair of rail cards.
        This visually ties the preview to the selector instead of the
        gallery and guarantees the panel never jumps when switching
        between CrMo and Ti-MX.
      */
      const width = Math.min(
        groupRect.width,
        window.innerWidth - edge * 2
      );

      this.railHoverPreview.style.width =
        `${Math.round(width)}px`;

      /*
        Width affects height because the preview uses a fixed aspect
        ratio, so read the real dimensions after setting width.
      */
      const previewRect =
        this.railHoverPreview.getBoundingClientRect();

      let left = groupRect.left;

      /*
        Preferred position is directly above the complete rail-card
        group. If the viewport is too shallow, place it directly below.
        Either way the panel stays anchored to the same selector block.
      */
      let top =
        groupRect.top -
        previewRect.height -
        gap;

      if (top < edge) {
        top =
          groupRect.bottom + gap;
      }

      left = Math.max(
        edge,
        Math.min(
          left,
          window.innerWidth -
            previewRect.width -
            edge
        )
      );

      top = Math.max(
        edge,
        Math.min(
          top,
          window.innerHeight -
            previewRect.height -
            edge
        )
      );

      this.railHoverPreview.style.left =
        `${Math.round(left)}px`;

      this.railHoverPreview.style.top =
        `${Math.round(top)}px`;
    }

    setRailHoverPreviewImage(source) {
      if (
        !source ||
        !this.railHoverPreviewImages
      ) {
        return;
      }

      if (
        this.railPreviewCurrentSource ===
        source
      ) {
        return;
      }

      const currentIndex =
        this.railPreviewActiveImageIndex;

      const nextIndex =
        currentIndex === 0 ? 1 : 0;

      const currentImage =
        this.railHoverPreviewImages[
          currentIndex
        ];

      const nextImage =
        this.railHoverPreviewImages[
          nextIndex
        ];

      nextImage.src = source;

      window.requestAnimationFrame(() => {
        nextImage.classList.add(
          'monarch-rail-hover-preview__image--active'
        );

        currentImage.classList.remove(
          'monarch-rail-hover-preview__image--active'
        );
      });

      this.railPreviewActiveImageIndex =
        nextIndex;

      this.railPreviewCurrentSource =
        source;
    }

    upgradeRailHoverPreviewImage(
      cardSource
    ) {
      const highResSource =
        this.getRailHoverPreviewSource(
          cardSource
        );

      if (
        !highResSource ||
        highResSource === cardSource
      ) {
        return;
      }

      const cachedImage =
        this.railPreviewPreloadCache
          ? this.railPreviewPreloadCache.get(
              highResSource
            )
          : null;

      if (
        cachedImage &&
        cachedImage.complete &&
        cachedImage.naturalWidth
      ) {
        this.setRailHoverPreviewImage(
          highResSource
        );

        return;
      }

      const loadToken =
        ++this.railPreviewLoadToken;

      const highResImage =
        cachedImage || new Image();

      highResImage.decoding = 'async';

      if ('fetchPriority' in highResImage) {
        highResImage.fetchPriority =
          'low';
      }

      if (
        this.railPreviewPreloadCache &&
        !this.railPreviewPreloadCache.has(
          highResSource
        )
      ) {
        this.railPreviewPreloadCache.set(
          highResSource,
          highResImage
        );
      }

      highResImage.onload = () => {
        if (
          loadToken !==
            this.railPreviewLoadToken ||
          this.railPreviewActiveCardSource !==
            cardSource
        ) {
          return;
        }

        this.setRailHoverPreviewImage(
          highResSource
        );
      };

      if (!highResImage.src) {
        highResImage.src =
          highResSource;
      }
    }

    showRailHoverPreview(railMedia) {
      if (
        !this.railHoverPreview ||
        !railMedia
      ) {
        return;
      }

      const image = railMedia.querySelector(
        '[data-monarch-rail-image]'
      );

      if (!image) return;

      const cardSource =
        image.currentSrc ||
        image.getAttribute('src') ||
        '';

      if (!cardSource) return;

      const highResSource =
        this.getRailHoverPreviewSource(
          cardSource
        );

      const cachedImage =
        this.railPreviewPreloadCache &&
        highResSource
          ? this.railPreviewPreloadCache.get(
              highResSource
            )
          : null;

      this.railPreviewRailMedia =
        railMedia;

      this.railPreviewActiveCardSource =
        cardSource;

      /*
        If the 1000px version is already cached, show it immediately.
        Otherwise show the existing card image without waiting, then
        upgrade invisibly as soon as the sharper file arrives.
      */
      if (
        cachedImage &&
        cachedImage.complete &&
        cachedImage.naturalWidth
      ) {
        this.setRailHoverPreviewImage(
          highResSource
        );
      } else {
        this.setRailHoverPreviewImage(
          cardSource
        );
      }

      this.positionRailHoverPreview();

      window.requestAnimationFrame(() => {
        if (!this.railHoverPreview) {
          return;
        }

        this.railHoverPreview.classList.add(
          'monarch-rail-hover-preview--visible'
        );
      });

      if (
        !cachedImage ||
        !cachedImage.complete ||
        !cachedImage.naturalWidth
      ) {
        this.upgradeRailHoverPreviewImage(
          cardSource
        );
      }
    }

    hideRailHoverPreview(immediate = false) {
      if (!this.railHoverPreview) {
        return;
      }

      if (this.railPreviewShowTimer) {
        window.clearTimeout(
          this.railPreviewShowTimer
        );

        this.railPreviewShowTimer = null;
      }

      if (this.railPreviewHideTimer) {
        window.clearTimeout(
          this.railPreviewHideTimer
        );

        this.railPreviewHideTimer = null;
      }

      this.railPreviewLoadToken += 1;
      this.railPreviewRailMedia = null;
      this.railPreviewActiveCardSource =
        '';

      this.railHoverPreview.classList.remove(
        'monarch-rail-hover-preview--visible'
      );

      if (immediate) {
        this.railHoverPreview.style.transition =
          'none';

        window.requestAnimationFrame(() => {
          if (!this.railHoverPreview) {
            return;
          }

          this.railHoverPreview.style.removeProperty(
            'transition'
          );
        });
      }
    }

    setupVariantImageHoverZoom() {
      if (!this.mediaGallery) return;

      this.variantZoomMediaQuery = window.matchMedia(
        '(min-width: 992px) and (hover: hover) and (pointer: fine)'
      );

      const styleId = 'monarch-variant-image-hover-zoom-styles';

      if (!document.getElementById(styleId)) {
        const style = document.createElement('style');

        style.id = styleId;
        style.textContent = `
          @media
            (min-width: 992px) and
            (hover: hover) and
            (pointer: fine) {
            media-gallery .monarch-variant-image-zoom {
              position: relative;
              overflow: hidden;
              cursor: zoom-in;
            }

            media-gallery .monarch-variant-image-zoom img {
              transform-origin:
                var(--monarch-zoom-x, 50%)
                var(--monarch-zoom-y, 50%);
              transition:
                transform 180ms ease-out;
              will-change: transform;
            }

            media-gallery
              .monarch-variant-image-zoom.monarch-variant-image-zoom--active
              img {
              transform: scale(1.5);
              transition-duration: 90ms;
            }
          }

          @media
            (prefers-reduced-motion: reduce) {
            media-gallery .monarch-variant-image-zoom img {
              transition: none !important;
            }
          }
        `;

        document.head.appendChild(style);
      }

      this.handleVariantZoomPointerMove = (event) => {
        if (
          !this.variantZoomMediaQuery ||
          !this.variantZoomMediaQuery.matches
        ) {
          return;
        }

        const item = event.target.closest(
          '.monarch-variant-image-zoom'
        );

        if (
          !item ||
          !this.mediaGallery.contains(item)
        ) {
          return;
        }

        const rect = item.getBoundingClientRect();

        if (!rect.width || !rect.height) return;

        const x = Math.max(
          0,
          Math.min(
            100,
            ((event.clientX - rect.left) / rect.width) * 100
          )
        );

        const y = Math.max(
          0,
          Math.min(
            100,
            ((event.clientY - rect.top) / rect.height) * 100
          )
        );

        item.style.setProperty(
          '--monarch-zoom-x',
          `${x.toFixed(2)}%`
        );
        item.style.setProperty(
          '--monarch-zoom-y',
          `${y.toFixed(2)}%`
        );
      };

      this.handleVariantZoomPointerOver = (event) => {
        if (
          !this.variantZoomMediaQuery ||
          !this.variantZoomMediaQuery.matches
        ) {
          return;
        }

        const item = event.target.closest(
          '.monarch-variant-image-zoom'
        );

        if (
          !item ||
          !this.mediaGallery.contains(item)
        ) {
          return;
        }

        item.classList.add(
          'monarch-variant-image-zoom--active'
        );
      };

      this.handleVariantZoomPointerOut = (event) => {
        const item = event.target.closest(
          '.monarch-variant-image-zoom'
        );

        if (
          !item ||
          !this.mediaGallery.contains(item)
        ) {
          return;
        }

        if (
          event.relatedTarget &&
          item.contains(event.relatedTarget)
        ) {
          return;
        }

        item.classList.remove(
          'monarch-variant-image-zoom--active'
        );

        item.style.removeProperty(
          '--monarch-zoom-x'
        );
        item.style.removeProperty(
          '--monarch-zoom-y'
        );
      };

      this.mediaGallery.addEventListener(
        'pointermove',
        this.handleVariantZoomPointerMove,
        { passive: true }
      );

      this.mediaGallery.addEventListener(
        'pointerover',
        this.handleVariantZoomPointerOver,
        { passive: true }
      );

      this.mediaGallery.addEventListener(
        'pointerout',
        this.handleVariantZoomPointerOut,
        { passive: true }
      );
    }

    updateVariantImageHoverZoom(
      selectedRail,
      selectedColour
    ) {
      if (!this.mediaGallery) return;

      this.mediaGallery
        .querySelectorAll(
          '.monarch-variant-image-zoom'
        )
        .forEach((item) => {
          item.classList.remove(
            'monarch-variant-image-zoom',
            'monarch-variant-image-zoom--active'
          );

          item.style.removeProperty(
            '--monarch-zoom-x'
          );
          item.style.removeProperty(
            '--monarch-zoom-y'
          );
        });

      if (!selectedRail || !selectedColour) return;

      const selectedVariant = this.getSelectedVariant(
        selectedRail,
        selectedColour
      );

      const selectedMediaId = selectedVariant
        ? this.getVariantMediaId(selectedVariant)
        : '';

      if (!selectedMediaId) return;

      const selectedMediaItem = Array.from(
        this.mediaGallery.querySelectorAll(
          '.main-image [data-media-id]'
        )
      ).find(
        (item) =>
          String(item.dataset.mediaId || '') ===
          String(selectedMediaId)
      );

      if (!selectedMediaItem) return;

      const image = selectedMediaItem.querySelector('img');

      if (!image) return;

      selectedMediaItem.classList.add(
        'monarch-variant-image-zoom'
      );
    }

    setupCleanGalleryThumbnails() {
      if (!this.mediaGallery) return;

      this.nativeThumbnailCarousel =
        this.mediaGallery.querySelector(
          'carousel-slider.thumbnails'
        );

      if (!this.nativeThumbnailCarousel) return;

      const existing =
        this.mediaGallery.querySelector(
          '[data-monarch-clean-thumbnails]'
        );

      if (existing) {
        this.cleanThumbnailContainer = existing;
        this.cleanThumbnailTrack =
          existing.querySelector(
            '[data-monarch-clean-thumbnail-track]'
          );
        return;
      }

      this.nativeThumbnailCarousel.classList.add(
        'monarch-gallery-native-thumbnails'
      );

      const styleId = 'monarch-gallery-clean-thumbnail-styles';

      if (!document.getElementById(styleId)) {
        const style = document.createElement('style');

        style.id = styleId;
        style.textContent = `
          media-gallery .monarch-gallery-native-thumbnails {
            display: none !important;
          }

          .monarch-gallery-clean-thumbnails {
            width: 100%;
            margin-top: 12px;
            overflow-x: auto;
            overflow-y: hidden;
            scrollbar-width: none;
            -ms-overflow-style: none;
            -webkit-overflow-scrolling: touch;
          }

          .monarch-gallery-clean-thumbnails::-webkit-scrollbar {
            display: none;
            width: 0;
            height: 0;
          }

          .monarch-gallery-clean-thumbnails__track {
            display: flex;
            width: max-content;
            min-width: 100%;
            align-items: flex-start;
            gap: 8px;
          }

          .monarch-gallery-clean-thumbnail {
            position: relative;
            display: block;
            flex: 0 0 65px;
            width: 65px;
            margin: 0;
            padding: 0 0 4px;
            overflow: hidden;
            color: inherit;
            background: transparent;
            border: 0;
            border-bottom: 3px solid transparent;
            border-radius: 0;
            cursor: pointer;
            appearance: none;
          }

          .monarch-gallery-clean-thumbnail:hover {
            border-bottom-color: rgba(0, 0, 0, 0.35);
          }

          .monarch-gallery-clean-thumbnail[aria-current='true'] {
            border-bottom-color: #000000;
          }

          .monarch-gallery-clean-thumbnail:focus-visible {
            outline: 2px solid #05c3dd;
            outline-offset: 2px;
          }

          .monarch-gallery-clean-thumbnail .img-ar {
            width: 100%;
          }

          .monarch-gallery-clean-thumbnail img {
            display: block;
            width: 100%;
            height: auto;
          }

          @media (max-width: 767px) {
            .monarch-gallery-clean-thumbnails {
              margin-top: 10px;
            }

            .monarch-gallery-clean-thumbnails__track {
              gap: 7px;
            }

            .monarch-gallery-clean-thumbnail {
              flex-basis: 58px;
              width: 58px;
            }
          }
        `;

        document.head.appendChild(style);
      }

      const container = document.createElement('div');

      container.className =
        'monarch-gallery-clean-thumbnails';
      container.dataset.monarchCleanThumbnails = 'true';

      const track = document.createElement('div');

      track.className =
        'monarch-gallery-clean-thumbnails__track';
      track.dataset.monarchCleanThumbnailTrack = 'true';

      container.appendChild(track);

      this.nativeThumbnailCarousel.insertAdjacentElement(
        'afterend',
        container
      );

      this.cleanThumbnailContainer = container;
      this.cleanThumbnailTrack = track;
    }

    updateGalleryMedia(selectedRail, selectedColour) {
      if (!this.mediaGallery) {
        this.mediaGallery = this.findMediaGallery();
      }

      if (!this.mediaGallery) return;

      if (
        !this.nativeThumbnailCarousel ||
        !this.cleanThumbnailTrack
      ) {
        this.setupCleanGalleryThumbnails();
      }

      if (
        !this.nativeThumbnailCarousel ||
        !this.cleanThumbnailTrack
      ) {
        return;
      }

      const selectedVariant = this.getSelectedVariant(
        selectedRail,
        selectedColour
      );

      const selectedMediaId = selectedVariant
        ? this.getVariantMediaId(selectedVariant)
        : '';

      this.renderCleanGalleryThumbnails(
        selectedMediaId
      );

      if (selectedMediaId) {
        this.scheduleGalleryMediaActivation(
          selectedMediaId
        );
      }
    }

    renderCleanGalleryThumbnails(selectedMediaId) {
      const sharedHeroMediaId = this.getFeaturedMediaId();
      const variantMediaIds = this.getAllVariantMediaIds();

      if (!variantMediaIds.size) return;

      const sourceItems = Array.from(
        this.nativeThumbnailCarousel.querySelectorAll(
          '[data-media-id]'
        )
      );

      this.cleanThumbnailTrack.innerHTML = '';

      sourceItems.forEach((sourceItem) => {
        const mediaId = String(
          sourceItem.dataset.mediaId || ''
        );

        const isVariantMedia =
          variantMediaIds.has(mediaId);

        const isSharedHero =
          sharedHeroMediaId &&
          mediaId === sharedHeroMediaId;

        const isSelectedVariant =
          selectedMediaId &&
          mediaId === selectedMediaId;

        const shouldShow =
          !isVariantMedia ||
          isSharedHero ||
          isSelectedVariant;

        if (!shouldShow) return;

        const sourceLink =
          sourceItem.querySelector('a.thumbnail');

        if (!sourceLink) return;

        const button = document.createElement('button');

        button.type = 'button';
        button.className =
          `${sourceLink.className} monarch-gallery-clean-thumbnail`;
        button.dataset.mediaId = mediaId;
        button.innerHTML = sourceLink.innerHTML;

        const image = sourceLink.querySelector('img');
        const altText =
          image && image.alt
            ? image.alt.trim()
            : '';

        button.setAttribute(
          'aria-label',
          altText
            ? `View ${altText}`
            : 'View product media'
        );

        const isCurrent =
          selectedMediaId
            ? mediaId === selectedMediaId
            : mediaId === sharedHeroMediaId;

        button.setAttribute(
          'aria-current',
          isCurrent ? 'true' : 'false'
        );

        button.addEventListener('click', () => {
          this.activateGalleryMedia(mediaId);

          this.cleanThumbnailTrack
            .querySelectorAll(
              '.monarch-gallery-clean-thumbnail'
            )
            .forEach((thumbnail) => {
              thumbnail.setAttribute(
                'aria-current',
                thumbnail.dataset.mediaId === mediaId
                  ? 'true'
                  : 'false'
              );
            });

          this.centerCleanThumbnail(
            button,
            'smooth'
          );
        });

        this.cleanThumbnailTrack.appendChild(button);
      });

      const activeThumbnail =
        this.cleanThumbnailTrack.querySelector(
          '.monarch-gallery-clean-thumbnail[aria-current="true"]'
        );

      if (activeThumbnail) {
        window.requestAnimationFrame(() => {
          this.centerCleanThumbnail(
            activeThumbnail,
            'smooth'
          );
        });
      }
    }

    centerCleanThumbnail(thumbnail, behavior = 'smooth') {
      if (
        !thumbnail ||
        !this.cleanThumbnailContainer
      ) {
        return;
      }

      const container =
        this.cleanThumbnailContainer;

      const thumbnailCentre =
        thumbnail.offsetLeft +
        thumbnail.offsetWidth / 2;

      const targetLeft =
        thumbnailCentre -
        container.clientWidth / 2;

      const maxLeft =
        Math.max(
          0,
          container.scrollWidth -
          container.clientWidth
        );

      container.scrollTo({
        left: Math.max(
          0,
          Math.min(targetLeft, maxLeft)
        ),
        behavior
      });
    }

    scheduleGalleryMediaActivation(mediaId) {
      this.pendingGalleryMediaId = mediaId;

      if (this.galleryActivationFrame) {
        window.cancelAnimationFrame(
          this.galleryActivationFrame
        );
      }

      this.galleryActivationFrame =
        window.requestAnimationFrame(() => {
          this.galleryActivationFrame =
            window.requestAnimationFrame(() => {
              const targetMediaId =
                this.pendingGalleryMediaId;

              this.pendingGalleryMediaId = '';

              if (targetMediaId) {
                this.activateGalleryMedia(
                  targetMediaId
                );
              }
            });
        });
    }

    activateGalleryMedia(mediaId) {
      if (
        !mediaId ||
        !this.nativeThumbnailCarousel
      ) {
        return;
      }

      this.setupGalleryVideos();

      window.requestAnimationFrame(() => {
        this.setupGalleryVideos();
        this.scheduleAdaptiveGallerySticky();

        const activeVideo =
          this.mediaGallery.querySelector(
            '.slider__item.is-active video'
          );

        if (activeVideo) {
          this.updateFloatingGalleryVideoProgress(
            activeVideo
          );
        } else if (this.galleryVideoProgressOverlay) {
          this.galleryVideoProgressOverlay.style.display =
            'none';
        }

        const selectedRail = this.getCheckedOptionValue(
          this.railOptionName
        );
        const selectedColour = this.getCheckedOptionValue(
          this.colourOptionName
        );

        this.updateVariantImageHoverZoom(
          selectedRail,
          selectedColour
        );
      });

      const sourceItem =
        Array.from(
          this.nativeThumbnailCarousel.querySelectorAll(
            '[data-media-id]'
          )
        ).find(
          (item) =>
            String(item.dataset.mediaId || '') ===
            String(mediaId)
        );

      const sourceLink =
        sourceItem
          ? sourceItem.querySelector('a.thumbnail')
          : null;

      if (sourceLink) {
        sourceLink.click();
        return;
      }

      /*
        Fallback only. In normal Symmetry operation the matching
        source thumbnail exists and the click above lets the
        theme perform its own media switching.
      */
      const mainSlider =
        this.mediaGallery.querySelector(
          '.main-image .slider'
        );

      const mainItem =
        Array.from(
          this.mediaGallery.querySelectorAll(
            '.main-image [data-media-id]'
          )
        ).find(
          (item) =>
            String(item.dataset.mediaId || '') ===
            String(mediaId)
        );

      if (mainSlider && mainItem) {
        mainSlider.scrollTo({
          left: mainItem.offsetLeft,
          behavior: 'auto'
        });
      }
    }

    getColourImageKey(colourValue) {
      const value = (colourValue || '').toLowerCase();

      if (value.includes('blue')) return 'blue';
      if (value.includes('red')) return 'red';
      if (value.includes('orange') || value.includes('mango')) return 'orange';
      if (
        value.includes('grey') ||
        value.includes('gray') ||
        value.includes('slate')
      ) {
        return 'grey';
      }

      return 'black';
    }

    getRailImageSource(button, imageKey) {
      const property =
        `image${imageKey.charAt(0).toUpperCase()}${imageKey.slice(1)}`;

      return button.dataset[property] || '';
    }

    updateRailImages(selectedColour) {
      const imageKey = this.getColourImageKey(selectedColour);

      this.railButtons.forEach((button) => {
        const image = button.querySelector('[data-monarch-rail-image]');

        if (!image) return;

        const source = this.getRailImageSource(button, imageKey);

        if (source && image.src !== source) {
          image.src = source;
        }
      });
    }

    preloadImage(source) {
      if (!source) return;

      const alreadyPreloaded = this.preloadedRailImages.some(
        (image) => image.src === source
      );

      if (alreadyPreloaded) return;

      const image = new Image();

      image.decoding = 'async';

      if ('fetchPriority' in image) {
        image.fetchPriority = 'low';
      }

      image.src = source;
      this.preloadedRailImages.push(image);
    }

    preloadColourImages(colourValue) {
      const imageKey = this.getColourImageKey(colourValue);

      this.railButtons.forEach((button) => {
        this.preloadImage(
          this.getRailImageSource(button, imageKey)
        );
      });
    }

    preloadAllRailImages() {
      const imageKeys = [
        'blue',
        'black',
        'red',
        'orange',
        'grey'
      ];

      this.railButtons.forEach((button) => {
        imageKeys.forEach((imageKey) => {
          this.preloadImage(
            this.getRailImageSource(button, imageKey)
          );
        });
      });
    }

    scheduleRailImagePreload() {
      if (this.dataset.preloadScheduled === 'true') return;

      this.dataset.preloadScheduled = 'true';

      const preloadWhenIdle = () => {
        const preload = () => {
          this.preloadAllRailImages();
        };

        if ('requestIdleCallback' in window) {
          window.requestIdleCallback(preload, {
            timeout: 2500
          });
        } else {
          window.setTimeout(preload, 900);
        }
      };

      /*
        Performance pass:
        Do not let the ten optional 600px rail/colour images compete
        with the product hero, theme CSS, fonts or other above-fold
        resources during initial load.

        If the shopper selects a colour before this runs, the existing
        preloadColourImages() call still starts that exact CrMo/Ti-MX
        pair immediately.
      */
      if (document.readyState === 'complete') {
        preloadWhenIdle();
      } else {
        window.addEventListener('load', preloadWhenIdle, {
          once: true
        });
      }
    }

    getVariants() {
      if (
        !this.variantData ||
        !Array.isArray(this.variantData.variants)
      ) {
        return [];
      }

      return this.variantData.variants;
    }

    getMatchingVariants(railValue, colourValue) {
      return this.getVariants().filter((variant) => {
        const options = variant.options || [];

        const railMatches = railValue
          ? options[this.railOptionIndex] === railValue
          : true;

        const colourMatches = colourValue
          ? options[this.colourOptionIndex] === colourValue
          : true;

        return railMatches && colourMatches;
      });
    }

    hasAvailableVariant(railValue, colourValue) {
      return this.getMatchingVariants(
        railValue,
        colourValue
      ).some((variant) => variant.available === true);
    }

    updateAvailability(selectedRail, selectedColour) {
      this.railButtons.forEach((button) => {
        const railValue = button.dataset.railValue;

        const available = this.hasAvailableVariant(
          railValue,
          selectedColour || ''
        );

        this.setAvailabilityState(button, available);
      });

      this.colourButtons.forEach((button) => {
        const colourValue = button.dataset.colourValue;

        const available = this.hasAvailableVariant(
          selectedRail || '',
          colourValue
        );

        this.setAvailabilityState(button, available);
      });
    }

    setAvailabilityState(button, available) {
      button.dataset.available = available ? 'true' : 'false';

      const status = button.querySelector('[data-monarch-availability]');

      if (status) {
        status.textContent = available ? '' : 'Sold out';
      }

      const baseLabel = button.dataset.baseAriaLabel || '';

      button.setAttribute(
        'aria-label',
        available
          ? baseLabel
          : `${baseLabel}, currently sold out`
      );
    }

    getFormattedPrice(variant) {
      if (
        !variant ||
        !this.variantData ||
        !this.variantData.formatted
      ) {
        return '';
      }

      const formatted =
        this.variantData.formatted[String(variant.id)];

      return formatted && formatted.price
        ? formatted.price
        : '';
    }

    updateMonarchPrice(selectedRail, selectedColour) {
      if (!this.section) return;

      const price = this.section.querySelector(
        '.product-price .price'
      );

      if (!price) return;

      const defaultPrice = price.querySelector('.price__default');

      const currentPrice = defaultPrice
        ? defaultPrice.querySelector('.price__current')
        : null;

      const noVariantPrice = price.querySelector('.price__no-variant');
      const fromLabel = price.querySelector('.price__from');
      const priceWas = price.querySelector('.price__was');
      const unitPrice = price.querySelector('.unit-price');

      if (!defaultPrice || !currentPrice) return;

      const matches = this.getMatchingVariants(
        selectedRail || '',
        selectedColour || ''
      );

      if (!matches.length) return;

      let minVariant = matches[0];
      let maxVariant = matches[0];

      matches.forEach((variant) => {
        if (variant.price < minVariant.price) {
          minVariant = variant;
        }

        if (variant.price > maxVariant.price) {
          maxVariant = variant;
        }
      });

      const minPrice = this.getFormattedPrice(minVariant);
      const maxPrice = this.getFormattedPrice(maxVariant);

      if (!minPrice || !maxPrice) return;

      defaultPrice.hidden = false;

      if (noVariantPrice) {
        noVariantPrice.hidden = true;
      }

      if (fromLabel) {
        fromLabel.hidden = true;
      }

      if (priceWas) {
        priceWas.innerHTML = '';
      }

      if (unitPrice) {
        unitPrice.hidden = true;
      }

      price.classList.remove(
        'price--on-sale',
        'price--sold-out'
      );

      if (minVariant.price === maxVariant.price) {
        currentPrice.innerHTML = minPrice;
      } else {
        currentPrice.innerHTML =
          `${minPrice}<span class="monarch-price-range-separator" aria-hidden="true"> – </span>${maxPrice}`;
      }
    }

    updatePurchaseButton(selectedRail, selectedColour) {
      if (!this.productForm) return;

      const addButton = this.productForm.querySelector('[name="add"]');

      if (!addButton) return;

      const hasRail = Boolean(selectedRail);
      const hasColour = Boolean(selectedColour);

      if (hasRail && hasColour) {
        return;
      }

      addButton.disabled = true;

      if (!hasRail && !hasColour) {
        addButton.textContent = 'Choose rail & colour';
      } else if (!hasRail) {
        addButton.textContent = 'Choose a rail';
      } else {
        addButton.textContent = 'Choose a colour';
      }
    }

    openRailComparison() {
      const title = Array.from(
        document.querySelectorAll(
          '.monarch-details__summary-title'
        )
      ).find((element) => {
        const text = element.textContent || '';

        return (
          text.includes('CrMo') &&
          text.includes('Ti-MX')
        );
      });

      if (!title) return;

      const details = title.closest('details');

      const summary = details
        ? details.querySelector('summary')
        : null;

      const target = summary || title;

      if (details) {
        details.open = true;
      }

      window.requestAnimationFrame(() => {
        const rootStyles = window.getComputedStyle(
          document.documentElement
        );

        const stickyHeight = parseFloat(
          rootStyles.getPropertyValue(
            '--theme-sticky-header-height'
          )
        );

        const headerOffset = Number.isFinite(stickyHeight)
          ? stickyHeight
          : 60;

        const top =
          target.getBoundingClientRect().top +
          window.scrollY -
          headerOffset -
          16;

        const reduceMotion = window.matchMedia(
          '(prefers-reduced-motion: reduce)'
        ).matches;

        window.scrollTo({
          top: Math.max(0, top),
          behavior: reduceMotion
            ? 'auto'
            : 'smooth'
        });
      });
    }
  }

  customElements.define(
    'monarch-saddle-picker',
    MonarchSaddlePicker
  );
}
